import React, { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Upload, CheckCircle2, AlertTriangle, FileText } from "lucide-react";

export default function ImportCSV() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  async function doPreview(f) {
    if (!f) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }

  async function doImport() {
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/clients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Migration</div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Import from MS Access (CSV)</h1>
        <p className="text-sm mt-2" style={{ color: "var(--fb-text-muted)" }}>
          Export your Access data to CSV with columns like first_name, last_name, phone, email, address.
        </p>
      </div>

      <div className="fb-card p-8">
        <div className="border-2 border-dashed rounded-xl p-10 text-center" style={{ borderColor: "var(--fb-border)" }}>
          <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--fb-text-muted)" }} />
          <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>
            Drop a CSV file or click to browse
          </p>
          <input
            ref={inputRef}
            data-testid="csv-file-input"
            type="file"
            accept=".csv"
            onChange={(e) => { const f = e.target.files?.[0]; setFile(f); doPreview(f); }}
            className="block mx-auto"
          />
        </div>
      </div>

      {preview && (
        <div className="fb-card p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Preview ({preview.total_preview} rows)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr style={{ background: "var(--fb-bg)" }}>
                {preview.headers.map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {preview.rows.slice(0, 8).map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                    {preview.headers.map((h) => (
                      <td key={h} className="px-3 py-2 truncate max-w-[200px]">{r[h]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-6">
            <button data-testid="confirm-import-btn" onClick={doImport} disabled={loading}
                    className="fb-btn-primary px-5 py-3 rounded-lg font-semibold inline-flex items-center gap-2">
              <FileText className="w-4 h-4" /> {loading ? "Importing…" : "Import Clients"}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="fb-card p-6 flex items-start gap-3 fb-status-success">
          <CheckCircle2 className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">Import complete</div>
            <div className="text-sm">{result.imported} clients imported · {result.skipped} skipped</div>
          </div>
        </div>
      )}
      {error && (
        <div className="fb-card p-6 flex items-start gap-3 fb-status-error">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}
